import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft, Calendar, Share2, Check, Copy } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback to local data if API fails
import { updates as localUpdates } from '../data/unifiedEcosystem';

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  
  return url;
};

// Helper function to convert video URLs to embed URLs
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Google Drive - convert to preview/embed URL
  const gdriveFileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (gdriveFileMatch) {
    return `https://drive.google.com/file/d/${gdriveFileMatch[1]}/preview`;
  }
  
  const gdriveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (gdriveOpenMatch) {
    return `https://drive.google.com/file/d/${gdriveOpenMatch[1]}/preview`;
  }
  
  // If it's already an embed URL or other format, return as-is
  return url;
};

export const ContentItemPage = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [relatedItems, setRelatedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Try API first
        const response = await fetch(`${API_URL}/api/content/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setItem(data);
          
          // Track view (fire and forget)
          if (data.content_id) {
            fetch(`${API_URL}/api/content/${data.content_id}/view`, {
              method: 'POST'
            }).catch(() => {}); // Silently fail
          }
          
          // Fetch related content
          if (data.project) {
            const relatedRes = await fetch(`${API_URL}/api/content?project=${encodeURIComponent(data.project)}&limit=4`);
            if (relatedRes.ok) {
              const related = await relatedRes.json();
              setRelatedItems(related.filter(r => r.slug !== slug).slice(0, 3));
            }
          }
        } else {
          // Fallback to local data
          const localItem = localUpdates.find(u => u.slug === slug);
          if (localItem) {
            setItem({
              ...localItem,
              external_url: localItem.externalUrl,
              content_id: localItem.id
            });
            const related = localUpdates.filter(u => 
              u.slug !== slug && u.project === localItem.project
            ).slice(0, 3);
            setRelatedItems(related.map(r => ({
              ...r,
              external_url: r.externalUrl,
              content_id: r.id
            })));
          }
        }
      } catch (error) {
        // Fallback to local data on error
        const localItem = localUpdates.find(u => u.slug === slug);
        if (localItem) {
          setItem({
            ...localItem,
            external_url: localItem.externalUrl,
            content_id: localItem.id
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug]);

  const handleShare = async () => {
    // Use the API og endpoint for sharing - this has proper meta tags for social crawlers
    // Use window.location.origin to get the correct domain (culturalrevolution.io in production)
    const shareUrl = `${window.location.origin}/api/og/${item.slug}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center px-4">
        <h1 className="text-8xl font-bold mb-4">404</h1>
        <p className="text-muted-foreground text-sm uppercase tracking-widest mb-8">Page not found</p>
        <Link to="/">
          <button className="px-6 py-3 bg-foreground text-background font-bold text-sm uppercase tracking-widest hover:opacity-80">
            Back to Home
          </button>
        </Link>
      </div>
    );
  }

  // Construct canonical URL and SEO data
  const canonicalUrl = `${window.location.origin}/${item.slug}`;
  const seoTitle = item.seo_title || `${item.title} | Cultural Revolution`;
  const seoDescription = item.seo_description || item.dek;
  const ogImage = convertGoogleDriveUrl(item.og_image || item.image);

  return (
    <>
      {/* SEO / Social Meta Tags - Critical for proper sharing */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Cultural Revolution" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Article metadata */}
        {item.date && <meta property="article:published_time" content={item.date} />}
        {item.type && <meta property="article:section" content={item.type} />}
      </Helmet>

      <div className="min-h-screen" data-testid="content-item-page">
        {/* Back Navigation */}
        <section className="editorial-container py-6 border-b border-border">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="back-link">
              <button className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft size={14} />
                Back to Feed
              </button>
            </Link>
            
            {/* Share Button - Made more prominent */}
            <div className="relative group">
              <Button
                onClick={handleShare}
                className={`flex items-center gap-2 px-4 py-2 transition-all ${
                  copied 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-foreground hover:bg-foreground/90 text-background'
                }`}
                data-testid="share-button"
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    <span className="text-sm font-medium">Link Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 size={16} />
                    <span className="text-sm font-medium">Share Article</span>
                  </>
                )}
              </Button>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-muted text-foreground text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border border-border shadow-lg">
                Copies link with thumbnail for social media
              </div>
            </div>
          </div>
        </section>

        {/* Header */}
        <section className="editorial-container py-12 lg:py-16">
          <div className="editorial-narrow">
            {/* Meta Tags */}
            <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="content-meta">
              {/* Content Type Tag */}
              <span className="px-3 py-1 bg-primary/10 text-primary text-xs uppercase tracking-widest font-bold" data-testid="content-type">
                {item.type}
              </span>
              
              {/* Category Tag */}
              {item.category && (
                <span className="px-3 py-1 border border-border text-xs uppercase tracking-widest text-muted-foreground" data-testid="content-category">
                  {item.category}
                </span>
              )}
              
              {/* Date */}
              <span className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest" data-testid="content-date">
                <Calendar size={12} />
                {new Date(item.date).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
            </div>

            {/* Headline */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl editorial-serif leading-tight mb-6"
              data-testid="content-title"
            >
              {item.title}
            </h1>

            {/* Dek */}
            <p 
              className="text-lg lg:text-xl text-foreground/80 leading-relaxed mb-8"
              data-testid="content-dek"
            >
              {item.dek}
            </p>

            {/* Author Byline */}
            {item.author && (
              <div className="mb-8 pb-6 border-b border-border" data-testid="author-byline">
                <p className="text-sm text-muted-foreground">
                  <span className="uppercase tracking-widest">By</span>{' '}
                  <span className="text-foreground font-medium">{item.author}</span>
                </p>
              </div>
            )}

            {/* Attribution - Project or Artist */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {item.project && (
                <span className="uppercase tracking-wider" data-testid="content-project">
                  {item.project}
                </span>
              )}
              {item.artist && (
                <span className="uppercase tracking-wider" data-testid="content-artist">
                  {item.artist}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        {item.image && (
          <section className="editorial-container pb-12">
            <div className="editorial-narrow">
              <div className="relative aspect-[16/9] bg-black overflow-hidden" data-testid="content-image-container">
                <img
                  src={convertGoogleDriveUrl(item.image)}
                  alt={item.title}
                  className="w-full h-full object-cover opacity-90"
                  data-testid="content-image"
                />
              </div>
            </div>
          </section>
        )}

        {/* Video Embed */}
        {item.video_url && (
          <section className="editorial-container pb-12" data-testid="video-section">
            <div className="editorial-narrow">
              <div className="relative aspect-video bg-black overflow-hidden">
                <iframe
                  src={getVideoEmbedUrl(item.video_url)}
                  title={item.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="content-video"
                />
              </div>
            </div>
          </section>
        )}

        {/* Body Text - Rich Text HTML */}
        {item.body && (
          <section className="editorial-container pb-12" data-testid="body-section">
            <div className="editorial-narrow">
              <div 
                className="rich-text-content text-foreground/80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.body }}
              />
            </div>
          </section>
        )}

        {/* Source Attribution & CTA - The Key Outbound Link */}
        <section className="editorial-container py-12 border-t border-border">
          <div className="editorial-narrow">
            <div className="bg-muted/20 border border-border p-8" data-testid="source-section">
              {item.source && (
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-4" data-testid="source-name">
                  Originally published on {item.source}
                </p>
              )}
              
              {item.external_url ? (
                <a
                  href={item.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-foreground text-background text-sm uppercase tracking-widest font-medium hover:opacity-90 transition-opacity"
                  data-testid="read-more-cta"
                >
                  Read more on {item.source}
                  <ExternalLink size={14} />
                </a>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  This content is hosted on Cultural Revolution.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Ecosystem Context */}
        <section className="editorial-container py-12 border-t border-border">
          <div className="editorial-narrow">
            <p className="text-base text-foreground/60 leading-relaxed">
              Cultural Revolution aggregates coverage, updates, and creator content from across the ecosystem. 
              This canonical page exists for sharing and discovery. The full content lives at its original source.
            </p>
          </div>
        </section>

        {/* Related Content */}
        {relatedItems.length > 0 && (
          <section className="editorial-container py-12 border-t border-border">
            <div className="editorial-narrow">
              <h2 className="text-2xl editorial-serif mb-8">Related</h2>
              <div className="space-y-6">
                {relatedItems.map((related) => (
                  <Link 
                    key={related.content_id || related.id}
                    to={`/${related.slug}`}
                    className="block group"
                    data-testid={`related-item-${related.slug}`}
                  >
                    <div className="flex gap-6 items-start">
                      <div className="w-24 h-16 flex-shrink-0 bg-black overflow-hidden">
                        <img 
                          src={related.image} 
                          alt={related.title}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest mb-1">
                          <span>{related.type}</span>
                          {related.source && (
                            <>
                              <span>·</span>
                              <span>{related.source}</span>
                            </>
                          )}
                        </div>
                        <h3 className="text-base editorial-serif group-hover:accent-red transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ContentItemPage;
